import { Check } from 'lucide-react';

import { useTranslation } from '@splitfiesta/i18n';
import { Card } from '@splitfiesta/ui';

import { type BadgeStore, StoreBadge } from '@/components/common/StoreBadge';

export interface InstallCardProps {
  payer: string;
  /** One store when the browser has one, both on a desktop. */
  stores: BadgeStore[];
  /** People on the bill who have the app, the visitor left out. */
  friends: number;
}

/** The one pitch after a pick; the page decides it shows once per browser. */
export function InstallCard({ payer, stores, friends }: InstallCardProps) {
  const { t } = useTranslation('claimLink');
  const lines = [t('install.payBack', { payer }), t('install.scan')];

  return (
    <Card noHoverEffect>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
        {t('install.title')}
      </h2>
      <p className="mt-1 text-base text-gray-700 dark:text-gray-300">
        {t('install.lead', { payer })}
      </p>
      <ul className="mt-3 space-y-2">
        {lines.map(line => (
          <li
            key={line}
            className="flex items-start gap-2 text-base text-gray-700 dark:text-gray-300"
          >
            <Check
              aria-hidden="true"
              className="mt-1 h-4 w-4 shrink-0 text-green-600 dark:text-green-400"
            />
            <span className="min-w-0 flex-1">{line}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-50">
        {t('install.cta')}
      </p>
      <div className="mt-2 flex flex-wrap gap-3">
        {stores.map(store => (
          <StoreBadge key={store} store={store} />
        ))}
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        {friends > 0
          ? t('install.friends', { count: friends })
          : t('install.noFriends')}
      </p>
    </Card>
  );
}

export default InstallCard;
